import React, {useCallback, useEffect, useRef} from 'react';
import {observer} from 'mobx-react';
import filtersStore, {FrameType} from './filters-store/filters-store';
import storePageDataSource from '../../storepage-datasource';
import {Portal} from '../../../util';
import {PriceRangeSlider} from '../price-range/price-range-slider';
import {OptionFilterList} from './option-search/option-filter-list';

const frameTypes = [
    {label: 'Tassel Frame', value: FrameType.Tassel},
    {label: 'Diploma Frame', value: FrameType.Diploma},
    {label: 'Certificate Frame', value: FrameType.Certificate},
];

const GRADUATION_YEAR_ERROR_ID = 'graduation-year-error';

type FiltersProps = {
    isOpen: boolean;
    onClose: () => void;
};

export const Filters: React.FC<FiltersProps> = observer(({isOpen, onClose}) => {
    const isBestSellersPage = window.location.pathname.includes('best-sellers');
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (isOpen) {
            closeButtonRef.current?.focus();
        }
    }, [isOpen]);

    const handleSortChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        filtersStore.updateFilter('sort', e.target.value);
    }, []);

    const handleSchoolSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        filtersStore.onSchoolSearch(e.target.value);
    }, []);

    const schoolSearchLabel = Portal && Portal.PortalID < 1 ? 'school' : Portal?.PortalID === 1 ? 'organization' : Portal?.PortalID === 2 ? 'Military Force' : 'school';

    return (
        <aside
            id="store-filters-container"
            className={isOpen ? 'filters-visible' : undefined}
            role={isOpen ? 'dialog' : undefined}
            aria-modal={isOpen ? true : undefined}
            aria-label="Product filters"
        >
            <div id="store-filters-x">
                <button
                    type="button"
                    ref={closeButtonRef}
                    onClick={onClose}
                    aria-label="Close Filter Menu"
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                    }}
                >
                    <i
                        className="fas fa-times"
                        aria-hidden="true"
                    />
                </button>
            </div>

            <div id="sort-by-container">
                <label htmlFor="sort-by">
                    <strong>Sort by</strong>
                </label>
                <select
                    name="sort-by"
                    id="sort-by"
                    className="form-select d-inline-block"
                    onChange={handleSortChange}
                    value={filtersStore.filters.sort}
                >
                    <option value="0">Featured</option>
                    <option value="1">Price: Low to High</option>
                    <option value="2">Price: High to Low</option>
                    <option value="3">Best Selling</option>
                    {Portal?.PortalID !== 3 && <option value="4">Best Reviews</option>}
                    {Portal?.PortalID !== 3 && <option value="5">Fast Shipping</option>}
                </select>
            </div>

            <hr />

            <div id="price-range-container">
                <PriceRangeSlider />
            </div>

            <hr />

            <div id="search-school-container">
                <label htmlFor="school-search">
                    {Portal && Portal.PortalID < 1 && <strong>Search for your school</strong>}
                    {Portal?.PortalID === 1 && <strong>Search for your organization</strong>}
                    {Portal?.PortalID === 2 && <strong>Search for your Military Force</strong>}
                    {Portal?.PortalID === 3 && <strong>Search for your school/state</strong>}
                </label>

                <input
                    id="school-search"
                    type="search"
                    className="form-control d-inline-block"
                    value={filtersStore.schoolSearchDraft}
                    onChange={handleSchoolSearch}
                    readOnly={filtersStore.requestInFlight}
                    aria-invalid={!!filtersStore.schoolSearchError}
                    aria-describedby={filtersStore.schoolSearchError ? 'school-search-error' : undefined}
                    aria-label={`Search for your ${schoolSearchLabel}`}
                />
                {filtersStore.schoolSearchError && (
                    <div
                        id="school-search-error"
                        className="text-danger mt-1"
                        role="alert"
                        style={{fontSize: 13}}
                    >
                        {filtersStore.schoolSearchError}
                    </div>
                )}

                <div id="store-filters-apply">
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Apply filters and close filter menu"
                    >
                        Apply Filter
                    </button>
                </div>
            </div>

            <hr />

            <fieldset id="mouldings-container">
                <legend>Moulding Options</legend>
                <OptionFilterList
                    inputId="moulding-option-search"
                    listId="moulding-option-list"
                    clearLabel="Clear moulding search"
                    allLabel="All Mouldings"
                    emptyMessage="No mouldings match your search"
                    checkboxClassName="filter-moulding"
                    options={storePageDataSource.mouldingOptions}
                    selected={filtersStore.selectedFinish}
                    onToggle={(checked, value) => filtersStore.finishChanged(checked, value)}
                />
            </fieldset>

            <hr />

            {!isBestSellersPage && (
                <fieldset id="mats-container">
                    <legend>Mat Options</legend>
                    <OptionFilterList
                        inputId="mat-option-search"
                        listId="mat-option-list"
                        clearLabel="Clear mat search"
                        allLabel="All Mats"
                        emptyMessage="No mats match your search"
                        checkboxClassName="filter-mat"
                        options={storePageDataSource.matOptions}
                        selected={filtersStore.selectedMatCombo}
                        onToggle={(checked, value) => filtersStore.matComboChanged(checked, value)}
                    />
                </fieldset>
            )}

            {Portal?.PortalID === 3 && (
                <>
                    <hr />
                    <fieldset id="frame-type-container">
                        <legend>Frame Type</legend>
                        <div className="filters-scrollable">
                            {frameTypes.map(frame => (
                                <label key={frame.value}>
                                    <input
                                        type="checkbox"
                                        value={frame.value}
                                        checked={filtersStore.selectedFrameType.includes(frame.value)}
                                        onChange={e => filtersStore.frameTypeChanged(e.target.checked, e.target.value as FrameType)}
                                    />
                                    {frame.label}
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    <hr />
                    <div id="graduation-year-container">
                        <label htmlFor="graduation-year-search">
                            <strong>Graduation Year</strong>
                        </label>
                        <input
                            id="graduation-year-search"
                            className="form-control d-inline-block"
                            placeholder="Enter graduation year"
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            spellCheck={false}
                            value={filtersStore.selectedGraduationYear}
                            readOnly={filtersStore.loading}
                            onChange={e => filtersStore.graduationYearChanged(e.target.value)}
                            aria-invalid={filtersStore.graduationYearError ? true : undefined}
                            aria-describedby={filtersStore.graduationYearError ? GRADUATION_YEAR_ERROR_ID : undefined}
                        />
                        {filtersStore.graduationYearError && (
                            <div
                                id={GRADUATION_YEAR_ERROR_ID}
                                className="text-danger mt-1"
                                role="alert"
                                style={{fontSize: 13}}
                            >
                                {filtersStore.graduationYearError}
                            </div>
                        )}
                    </div>
                </>
            )}
        </aside>
    );
});
